import React, { useEffect, useMemo, useRef, useState } from 'react'

import PropTypes from 'prop-types'

import { findMatchingEntity } from './entityMatcher'

export const GENERIC_FORM_STOPWORDS = new Set([
  'the',
  'and',
  'for',
  'with',
  'from',
  'that',
  'this',
  'have',
  'been',
  'date',
  'type',
  'time',
  'name',
  'page',
  'report',
  'code',
  'status',
  'first',
  'last',
  'middle',
  'were',
  'was',
  'are',
  'is',
  'at',
  'of',
  'to',
  'in',
  'on',
  'by',
  'or',
  'as',
  'if',
  'all',
  'any',
  'not',
  'no',
  'yes',
  'injury',
  'injuries',
  'illness',
  'accident',
  'incident',
  'nature',
  'part',
  'body',
  'average',
  'weekly',
  'wage',
  'wages',
  'per',
  'paid',
  'hours',
  'days',
  'rate',
  'amount',
  'description',
  'statement',
  'employer',
  'employee',
  'claimant',
  'carrier',
  'state',
  'form',
  'section',
  'box',
  'number',
  'info',
  'information',
  'address',
  'addresses',
  'street',
  'city',
  'zip',
  'phone',
  'fax',
  'email',
  'site',
  'digit',
  'digits',
  'check',
  'one',
  'initial',
  'treatment',
])

export const hasSubstantiveToken = text => {
  if (!text) return false
  const tokens = text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(t => t.length >= 1)
  if (tokens.length === 0) return false
  const substantive = tokens.filter(t => !GENERIC_FORM_STOPWORDS.has(t))
  if (substantive.length === 0) return false
  // If the only substantive token is an isolated single digit (e.g. "9" from "9-digit"), it is not substantive
  if (substantive.length === 1 && /^\d$/.test(substantive[0])) return false
  return true
}

export const getBlockContext = (block, allBlocks = []) => {
  if (!block || !Array.isArray(allBlocks)) return ''
  // 1. Column header vertically above in the same column (dx < 0.12, dy between 0.005 and 0.15 on same page)
  const columnHeaders = allBlocks.filter(other => {
    if (other === block) return false
    if (other.page && block.page && other.page !== block.page) return false
    const dx = Math.abs((other.left || 0) - (block.left || 0))
    const dy = (block.top || 0) - (other.top || 0)
    return dx < 0.12 && dy > 0.005 && dy < 0.15 && other.text
  })
  // 2. Inline label directly on the left (dy < 0.02, dx between 0.01 and 0.35 on same page)
  const inlineHeaders = allBlocks.filter(other => {
    if (other === block) return false
    if (other.page && block.page && other.page !== block.page) return false
    const dy = Math.abs((other.top || 0) - (block.top || 0))
    const dx = (block.left || 0) - (other.left || 0)
    return dy < 0.02 && dx > 0.01 && dx < 0.35 && other.text
  })
  return [...columnHeaders, ...inlineHeaders].map(h => (h.text || '').toLowerCase()).join(' ')
}

/**
 * Computes bounding box highlight regions from Textract lineBlocks matching the active entity.
 */
export function computeImageHighlights({
  lineBlocks = [],
  activeSnippet = '',
  activeEntityValue = '',
  activeCategory = '',
  activeEntityField = '',
  activeLineNumber,
  activePage = 1,
}) {
  if (!lineBlocks || lineBlocks.length === 0) return []
  if (!activeSnippet && !activeEntityValue && !activeLineNumber) return []

  const normalize = str =>
    (str || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

  const cleanSnippet = normalize(activeSnippet)
  const cleanEntity = normalize(activeEntityValue)

  const noiseWords = new Set([
    'the',
    'and',
    'for',
    'with',
    'from',
    'that',
    'this',
    'have',
    'been',
    'date',
    'type',
    'time',
    'name',
    'page',
    'report',
  ])

  const entityWords = cleanEntity.split(/\s+/).filter(w => w.length >= 3 && !noiseWords.has(w))

  const isAppointmentField =
    (activeCategory || '').toLowerCase() === 'appointment' ||
    (activeEntityField || '').toLowerCase().includes('appointment') ||
    (activeEntityField || '').toLowerCase().includes('evaluation') ||
    (activeEntityField || '').toLowerCase().includes('visit') ||
    (activeEntityField || '').toLowerCase().includes('treatment')

  const isClaimantField =
    (activeEntityField || '').toLowerCase().includes('claimant') ||
    (activeEntityField || '').toLowerCase().includes('patient') ||
    (activeEntityField || '').toLowerCase() === 'name'

  const isDateField =
    (activeEntityField || '').toLowerCase().includes('date') ||
    (activeCategory || '').toLowerCase().includes('date') ||
    /\b\d{1,2}[\s/-]\d{1,2}[\s/-]\d{2,4}\b/.test(activeEntityValue || '')

  const isInjuryDateField = (activeEntityField || '').toLowerCase().includes('injury') && isDateField

  const matches = []

  lineBlocks.forEach((block, idx) => {
    if (activePage && block.page && block.page !== activePage) return

    const blockText = (block.text || '').trim()
    if (!blockText) return

    const cleanBlockText = normalize(blockText)
    if (!cleanBlockText) return

    const blockContext = isInjuryDateField ? getBlockContext(block, lineBlocks) : ''
    const isCompetingNotificationBlock =
      isInjuryDateField &&
      (/\b(?:notified|employer\s+notified|date\s+employer\s+notified|date\s+employer)\b/i.test(cleanBlockText) ||
        /\b(?:notified|employer\s+notified|date\s+employer\s+notified|date\s+employer)\b/i.test(blockContext)) &&
      !/\b(?:injury|accident|illness)\b/i.test(cleanBlockText) &&
      !/\b(?:injury|accident|illness)\b/i.test(blockContext)

    if (isCompetingNotificationBlock) {
      return
    }

    let isMatch = false
    let score = 0

    // 1. Direct line number match
    if (activeLineNumber && block.lineNumber === activeLineNumber) {
      isMatch = true
      score += 100
    }

    const isBlockPureDate =
      /^\d{1,2}[\s/-]\d{1,2}(?:[\s/-]\d{2,4})?$/.test(cleanBlockText) ||
      /^\d{4}[\s/-]\d{1,2}[\s/-]\d{1,2}$/.test(cleanBlockText)

    // 2. Exact substring match on cleanSnippet
    if (cleanSnippet && cleanSnippet.length >= 4) {
      const isCompetingNotification =
        isInjuryDateField && /\b(?:notified|employer\s+notified|date\s+employer\s+notified)\b/i.test(cleanSnippet)
      if (!isCompetingNotification) {
        if (cleanBlockText.includes(cleanSnippet)) {
          isMatch = true
          score += 85
        } else if (
          cleanSnippet.includes(cleanBlockText) &&
          cleanBlockText.length >= 8 &&
          hasSubstantiveToken(cleanBlockText) &&
          (!isBlockPureDate || (isDateField && !isAppointmentField))
        ) {
          isMatch = true
          score += 75
        }
      }
    }

    // 3. Exact substring match on cleanEntity
    if (cleanEntity && cleanEntity.length >= 3) {
      const isCompetingBlock =
        isInjuryDateField &&
        /\b(?:notified|employer\s+notified|date\s+employer\s+notified)\b/i.test(cleanBlockText) &&
        !/\b(?:injury|accident|illness)\b/i.test(cleanBlockText)
      if (!isCompetingBlock) {
        if (cleanBlockText.includes(cleanEntity)) {
          isMatch = true
          score += 90
        } else if (
          cleanEntity.includes(cleanBlockText) &&
          cleanBlockText.length >= 6 &&
          hasSubstantiveToken(cleanBlockText) &&
          (!isBlockPureDate || (isDateField && !isAppointmentField))
        ) {
          isMatch = true
          score += 65
        }
      }
    }

    // 4. Clause-based matching for snippet (split on punctuation)
    if (!isMatch && activeSnippet) {
      const clauses = activeSnippet
        .split(/[;.,\n]/)
        .map(c => normalize(c))
        .filter(c => c.length >= 8)
      for (const clause of clauses) {
        if (isInjuryDateField && /\b(?:notified|employer\s+notified|date\s+employer\s+notified)\b/i.test(clause)) {
          continue
        }
        if (cleanBlockText.includes(clause) && hasSubstantiveToken(clause)) {
          isMatch = true
          score += 55
          break
        } else if (
          clause.length >= 8 &&
          cleanBlockText.length >= 6 &&
          clause.includes(cleanBlockText) &&
          hasSubstantiveToken(cleanBlockText) &&
          (!isBlockPureDate || (isDateField && !isAppointmentField))
        ) {
          isMatch = true
          score += 55
          break
        }
      }
    }

    // 5. Token overlap matching for multi-word phrases (e.g. facility names, tests, injury descriptions)
    if (!isMatch && !isDateField && entityWords.length > 0) {
      const blockWords = new Set(cleanBlockText.split(/\s+/))
      const matchingCount = entityWords.filter(w => blockWords.has(w)).length
      if (matchingCount >= Math.min(2, entityWords.length) && matchingCount / entityWords.length >= 0.5) {
        isMatch = true
        score += 40 + matchingCount * 10
      }
    }

    // 6. Claimant / Patient label matching: e.g. "Patient: John Doe"
    if (!isMatch && isClaimantField && cleanEntity) {
      if (
        (cleanBlockText.includes('patient') || cleanBlockText.includes('claimant')) &&
        entityWords.some(w => cleanBlockText.includes(w))
      ) {
        isMatch = true
        score += 80
      }
    }

    // 7. Date matching for date fields
    if (!isMatch && isDateField && !isAppointmentField) {
      const isCompetingDateBlock =
        isInjuryDateField &&
        /\b(?:notified|employer\s+notified|date\s+employer\s+notified)\b/i.test(cleanBlockText) &&
        !/\b(?:injury|accident|illness)\b/i.test(cleanBlockText)
      if (!isCompetingDateBlock) {
        const dateMatch = (activeEntityValue || activeSnippet || '').match(
          /\b(\d{1,2})[\s/-](\d{1,2})[\s/-](\d{2,4})\b/,
        )
        if (dateMatch) {
          const m = dateMatch[1].padStart(2, '0')
          const d = dateMatch[2].padStart(2, '0')
          const y = dateMatch[3]
          if (
            cleanBlockText.includes(`${m}/${d}/${y}`) ||
            cleanBlockText.includes(`${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`) ||
            cleanBlockText.includes(`${m}-${d}-${y}`)
          ) {
            isMatch = true
            score += 75
          }
        }
      }
    }

    // 8. Appointment field multi-criteria: check treatment type or date
    if (!isMatch && isAppointmentField) {
      if (
        cleanBlockText.includes('treatment') ||
        cleanBlockText.includes('evaluation') ||
        cleanBlockText.includes('appointment')
      ) {
        if (
          entityWords.some(w => cleanBlockText.includes(w)) ||
          (cleanSnippet && cleanBlockText.includes(cleanSnippet))
        ) {
          isMatch = true
          score += 70
        }
      }
    }

    if (isMatch && isInjuryDateField) {
      if (
        /\b(?:injury|accident|illness)\b/i.test(blockContext) ||
        /\b(?:injury|accident|illness)\b/i.test(cleanBlockText)
      ) {
        score += 150
      }
    }

    if (isMatch) {
      const width = typeof block.width === 'number' ? block.width : 0
      const height = typeof block.height === 'number' ? block.height : 0
      const left = typeof block.left === 'number' ? block.left : 0
      const top = typeof block.top === 'number' ? block.top : 0

      if (width > 0 && height > 0) {
        matches.push({
          lineNumber: block.lineNumber,
          page: block.page,
          text: blockText,
          score,
          idx,
          // Padded bounding box in percentages (clamped 0..1)
          left: Math.max(0, left - 0.004),
          top: Math.max(0, top - 0.003),
          width: Math.min(1 - left, width + 0.008),
          height: Math.min(1 - top, height + 0.006),
        })
      }
    }
  })

  // Mark highest score as primary
  let highestScore = -1
  let primaryIndex = 0
  matches.forEach((m, i) => {
    if (m.score > highestScore) {
      highestScore = m.score
      primaryIndex = i
    }
  })

  const results = matches.map((m, i) => ({
    ...m,
    isPrimary: i === primaryIndex,
  }))

  // Primary match always sorted first, followed by score, then vertical position
  results.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0) || b.score - a.score || a.top - b.top)

  return results
}

export default function ImageHighlightViewer({
  url,
  fileName,
  lineBlocks = [],
  activePage = 1,
  activeLineNumber,
  activeSnippet = '',
  activeCategory = '',
  activeEntityField = '',
  activeEntityValue = '',
  entities = [],
  onSnippetClick,
  onShowPopover,
}) {
  const containerRef = useRef(null)
  const imageRef = useRef(null)
  const activeHighlightRef = useRef(null)

  const [zoom, setZoom] = useState(1.0)
  const [fitWidth, setFitWidth] = useState(true)
  const [showHighlights, setShowHighlights] = useState(true)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [transientBlock, setTransientBlock] = useState(null)

  const handleImageClick = e => {
    if (e.target.closest('.hitl-image-highlight-box')) return
    if (!imageRef.current || !lineBlocks || lineBlocks.length === 0) return

    const rect = imageRef.current.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const clickY = e.clientY - rect.top
    const relX = clickX / rect.width
    const relY = clickY / rect.height

    const pageBlocks = lineBlocks.filter(b => !b.page || b.page === (activePage || 1))
    const hitBlock = pageBlocks.find(b => {
      const left = b.left ?? b.Left ?? 0
      const top = b.top ?? b.Top ?? 0
      const width = b.width ?? b.Width ?? 0
      const height = b.height ?? b.Height ?? 0
      return relX >= left && relX <= left + width && relY >= top && relY <= top + height
    })

    if (hitBlock) {
      const text = (hitBlock.text || hitBlock.Text || '').trim()
      if (!text) return

      const matched = findMatchingEntity(text, entities)

      setTransientBlock(hitBlock)

      if (matched && onSnippetClick) {
        onSnippetClick(matched.extractedValue || matched.field, matched.id)
      }
      if (onShowPopover) {
        onShowPopover({
          text,
          sourceSnippet: text,
          page: hitBlock.page || activePage || 1,
          coords: {
            x: e.clientX,
            y: e.clientY,
            width: 180,
            height: 24,
          },
          matchedEntity: matched || null,
        })
      }
    } else {
      setTransientBlock(null)
    }
  }

  // Compute highlight boxes
  const highlightBoxes = useMemo(() => {
    return computeImageHighlights({
      lineBlocks,
      activeSnippet,
      activeEntityValue,
      activeCategory,
      activeEntityField,
      activeLineNumber,
      activePage,
    })
  }, [lineBlocks, activeSnippet, activeEntityValue, activeCategory, activeEntityField, activeLineNumber, activePage])

  // Auto-scroll to primary highlight whenever active entity/snippet changes, strictly confined to containerRef
  useEffect(() => {
    if (activeHighlightRef.current && containerRef.current) {
      const container = containerRef.current
      const targetEl = activeHighlightRef.current
      const containerRect = container.getBoundingClientRect()
      const targetRect = targetEl.getBoundingClientRect()
      const relativeTop = targetRect.top - containerRect.top + container.scrollTop
      const targetScrollTop = Math.max(0, relativeTop - container.clientHeight / 2 + targetRect.height / 2)

      if (typeof container.scrollTo === 'function') {
        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        })
      } else {
        container.scrollTop = targetScrollTop
      }
    }
  }, [activeSnippet, activeEntityValue, activeLineNumber, activePage, highlightBoxes])

  const handleZoomIn = () => {
    setFitWidth(false)
    setZoom(prev => Math.min(prev + 0.25, 3.0))
  }

  const handleZoomOut = () => {
    setFitWidth(false)
    setZoom(prev => Math.max(prev - 0.25, 0.5))
  }

  const handleResetZoom = () => {
    setFitWidth(false)
    setZoom(1.0)
  }

  const handleFitWidth = () => {
    setFitWidth(true)
    setZoom(1.0)
  }

  return (
    <div ref={containerRef} className="hitl-image-document-container position-relative">
      {/* Floating Toolbar */}
      <div className="hitl-image-toolbar d-flex align-items-center">
        <div className="btn-group btn-group-sm mr-2 shadow-sm">
          <button type="button" className="btn btn-dark btn-sm text-light" onClick={handleZoomOut} title="Zoom Out">
            <i className="fas fa-search-minus" />
          </button>
          <button
            type="button"
            className="btn btn-dark btn-sm text-light font-weight-bold"
            onClick={handleResetZoom}
            title="Actual Size (100%)"
            style={{ minWidth: '55px', fontSize: '0.75rem' }}
          >
            {fitWidth ? 'Fit' : `${Math.round(zoom * 100)}%`}
          </button>
          <button type="button" className="btn btn-dark btn-sm text-light" onClick={handleZoomIn} title="Zoom In">
            <i className="fas fa-search-plus" />
          </button>
        </div>

        <button
          type="button"
          className={`btn btn-sm shadow-sm mr-2 ${fitWidth ? 'btn-primary' : 'btn-dark text-light'}`}
          onClick={handleFitWidth}
          title="Fit to Width"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
        >
          <i className="fas fa-arrows-alt-h mr-1" /> Fit Width
        </button>

        <button
          type="button"
          className={`btn btn-sm shadow-sm ${
            showHighlights ? 'btn-warning text-dark font-weight-bold' : 'btn-secondary text-light'
          }`}
          onClick={() => setShowHighlights(prev => !prev)}
          title={showHighlights ? 'Hide Highlights' : 'Show Highlights'}
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
        >
          <i className="fas fa-highlighter mr-1" />
          {showHighlights ? 'Highlights ON' : 'Highlights OFF'}
        </button>

        {showHighlights && highlightBoxes.length > 0 && (
          <span
            className="badge badge-pill badge-warning text-dark ml-2 font-weight-bold shadow-sm"
            style={{ fontSize: '0.72rem' }}
          >
            {highlightBoxes.length} region{highlightBoxes.length > 1 ? 's' : ''} matched
          </span>
        )}
      </div>

      {/* Image Canvas Wrapper */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className="hitl-image-wrapper position-relative"
        onClick={handleImageClick}
        style={{
          width: fitWidth ? '100%' : 'auto',
          maxWidth: fitWidth ? '100%' : 'none',
          transform: !fitWidth && zoom !== 1.0 ? `scale(${zoom})` : 'none',
          transformOrigin: 'top center',
          transition: 'transform 0.15s ease-out',
          cursor: 'pointer',
        }}
      >
        <img
          ref={imageRef}
          src={url}
          alt={fileName}
          className="hitl-document-image d-block"
          style={{
            width: fitWidth ? '100%' : 'auto',
            height: 'auto',
            maxHeight: 'none',
          }}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Transient Hit-Test Selection Highlight */}
        {transientBlock && (
          <div
            className="hitl-transient-highlight"
            style={{
              position: 'absolute',
              top: `${(transientBlock.top ?? transientBlock.Top) * 100}%`,
              left: `${(transientBlock.left ?? transientBlock.Left) * 100}%`,
              width: `${(transientBlock.width ?? transientBlock.Width) * 100}%`,
              height: `${(transientBlock.height ?? transientBlock.Height) * 100}%`,
              borderRadius: '2px',
              pointerEvents: 'none',
              zIndex: 14,
            }}
          />
        )}

        {/* Highlight Overlays */}
        {imageLoaded &&
          showHighlights &&
          highlightBoxes.map((box, idx) => (
            <div
              key={idx}
              ref={box.isPrimary ? activeHighlightRef : null}
              className={`hitl-image-highlight-box ${box.isPrimary ? 'active-primary-highlight' : ''}`}
              style={{
                position: 'absolute',
                top: `${box.top * 100}%`,
                left: `${box.left * 100}%`,
                width: `${box.width * 100}%`,
                height: `${box.height * 100}%`,
              }}
              title={box.text}
              role="button"
              tabIndex={0}
              onClick={() => onSnippetClick && onSnippetClick(box.text)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onSnippetClick && onSnippetClick(box.text)
                }
              }}
            >
              {box.isPrimary && <span className="hitl-image-highlight-badge">{activeEntityField || 'Matched'}</span>}
            </div>
          ))}
      </div>
    </div>
  )
}

ImageHighlightViewer.propTypes = {
  url: PropTypes.string,
  fileName: PropTypes.string,
  lineBlocks: PropTypes.arrayOf(PropTypes.object),
  activePage: PropTypes.number,
  activeLineNumber: PropTypes.number,
  activeSnippet: PropTypes.string,
  activeCategory: PropTypes.string,
  activeEntityField: PropTypes.string,
  activeEntityValue: PropTypes.string,
  entities: PropTypes.arrayOf(PropTypes.object),
  onSnippetClick: PropTypes.func,
  onShowPopover: PropTypes.func,
}
